import { UNIT_KINDS, tileDef, tileDefensePercent, tileName, unitDef } from "@dailywars/core/engine";
import type { MovementType, TileDef } from "@dailywars/core/engine";
import { CODEX_MAP_THEMES } from "@dailywars/core/render/mapThemes";
import { Car, Crosshair, Footprints, Plane, Route } from "lucide-react";

import { CoinMoneyIcon } from "#/components/game/CoinMoneyIcon";
import { SectionHeader } from "#/components/SectionHeader";
import { Card } from "#/components/ui/card";
import { Infotip, InfotipContent, InfotipProvider, InfotipTrigger } from "#/components/ui/infotip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "#/components/ui/table";
import { getBuildingIconSrc } from "#/game/buildingSprites";

import {
  BUILDING_TILE_LABEL,
  CODEX_TILE_KINDS,
  TILE_DESCRIPTION,
  UNIT_DESCRIPTION,
} from "./-codex.data";
import { CodexTileIcon, CodexUnitIcon } from "./-codex.icons";

function formatRange({ minRange, maxRange }: { minRange: number; maxRange: number }): string {
  return minRange === maxRange ? String(minRange) : `${minRange}–${maxRange}`;
}

const MOVEMENT_TYPE_ICON = {
  foot: Footprints,
  vehicle: Car,
  air: Plane,
} satisfies Record<MovementType, typeof Footprints>;

const CODEX_DESCRIPTION_MIN_WIDTH = "min-w-48 whitespace-normal";
const CODEX_DESCRIPTION_CELL = `${CODEX_DESCRIPTION_MIN_WIDTH} text-sm leading-snug text-muted-foreground`;

function CodexMoveCell({ movement }: { movement: number }) {
  return (
    <span className="flex items-center gap-1.5 text-sm tabular-nums text-muted-foreground">
      <Route className="h-4 w-4 shrink-0" aria-hidden />
      {movement}
    </span>
  );
}

function CodexRangeCell({ minRange, maxRange }: { minRange: number; maxRange: number }) {
  return (
    <span className="flex items-center gap-1.5 text-sm tabular-nums text-muted-foreground">
      <Crosshair className="h-4 w-4 shrink-0" aria-hidden />
      {formatRange({ minRange, maxRange })}
    </span>
  );
}

function CodexUnitRow({ kind }: { kind: (typeof UNIT_KINDS)[number] }) {
  const def = unitDef(kind);
  const buildAtSites = def.buildAt;
  return (
    <TableRow>
      <TableCell className="py-3">
        <CodexUnitIcon kind={kind} />
      </TableCell>
      <TableCell className="font-semibold">{def.name}</TableCell>
      <TableCell className={CODEX_DESCRIPTION_CELL}>{UNIT_DESCRIPTION[kind]}</TableCell>
      <TableCell>
        <span className="flex items-center justify-end gap-1 tabular-nums text-muted-foreground">
          {def.cost}
          <CoinMoneyIcon size="sm" />
        </span>
      </TableCell>
      <TableCell>
        <CodexMoveCell movement={def.movement} />
      </TableCell>
      <TableCell>
        <CodexRangeCell minRange={def.minRange} maxRange={def.maxRange} />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {buildAtSites.map((site) => (
            <div key={site} className="flex items-center gap-2">
              <img
                src={getBuildingIconSrc(site, "p1")}
                alt=""
                className="h-7 w-7 shrink-0"
                aria-hidden
              />
              <span className="text-sm">{BUILDING_TILE_LABEL[site]}</span>
            </div>
          ))}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function CodexUnitsTable() {
  return (
    <section className="flex flex-col gap-2">
      <SectionHeader>Units</SectionHeader>
      <Card className="overflow-x-auto py-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted hover:bg-muted">
              <TableHead className="w-14 min-w-14" />
              <TableHead className="min-w-28">Name</TableHead>
              <TableHead className={CODEX_DESCRIPTION_MIN_WIDTH}>Description</TableHead>
              <TableHead className="w-16 min-w-16 text-right">Cost</TableHead>
              <TableHead className="w-16 min-w-16">Move</TableHead>
              <TableHead className="w-16 min-w-16">Range</TableHead>
              <TableHead className="min-w-32">Build at</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {UNIT_KINDS.map((kind) => (
              <CodexUnitRow key={kind} kind={kind} />
            ))}
          </TableBody>
        </Table>
      </Card>
    </section>
  );
}

function formatEnterCost(cost: number | null): string {
  return cost === null ? "—" : String(cost);
}

function CodexTileMoveCostCell({ enterCost }: { enterCost: TileDef["enterCost"] }) {
  const movementTypes = ["foot", "vehicle", "air"] as const satisfies readonly MovementType[];

  return (
    <div className="flex flex-col gap-1 text-sm text-muted-foreground">
      {movementTypes.map((mtype) => {
        const Icon = MOVEMENT_TYPE_ICON[mtype];
        return (
          <span key={mtype} className="flex items-center gap-1.5 tabular-nums">
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {formatEnterCost(enterCost[mtype])}
          </span>
        );
      })}
    </div>
  );
}

function CodexTileVariant({
  kind,
  theme,
}: {
  kind: (typeof CODEX_TILE_KINDS)[number];
  theme: (typeof CODEX_MAP_THEMES)[number]["id"];
}) {
  const name = tileName({ kind, theme });

  return (
    <Infotip>
      <InfotipTrigger aria-label={name} className="block">
        <CodexTileIcon kind={kind} theme={theme} />
      </InfotipTrigger>
      <InfotipContent>{name}</InfotipContent>
    </Infotip>
  );
}

function CodexTileVariants({ kind }: { kind: (typeof CODEX_TILE_KINDS)[number] }) {
  return (
    <div className="flex items-center gap-1">
      {CODEX_MAP_THEMES.map(({ id }) => (
        <CodexTileVariant key={id} kind={kind} theme={id} />
      ))}
    </div>
  );
}

function CodexTileRow({ kind }: { kind: (typeof CODEX_TILE_KINDS)[number] }) {
  const def = tileDef(kind);
  return (
    <TableRow>
      <TableCell className="py-3">
        <CodexTileIcon kind={kind} />
      </TableCell>
      <TableCell className="font-semibold">{def.name}</TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {tileDefensePercent({ kind })}%
      </TableCell>
      <TableCell>
        <CodexTileMoveCostCell enterCost={def.enterCost} />
      </TableCell>
      <TableCell className={CODEX_DESCRIPTION_CELL}>{TILE_DESCRIPTION[kind]}</TableCell>
      <TableCell>
        <CodexTileVariants kind={kind} />
      </TableCell>
    </TableRow>
  );
}

export function CodexTilesTable() {
  return (
    <section className="flex flex-col gap-2">
      <SectionHeader>Tiles</SectionHeader>
      <InfotipProvider delay={300}>
        <Card className="overflow-x-auto py-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead className="w-14 min-w-14" />
                <TableHead className="min-w-20">Name</TableHead>
                <TableHead className="w-16 min-w-16">Defense</TableHead>
                <TableHead className="w-16 min-w-16">Move cost</TableHead>
                <TableHead className={CODEX_DESCRIPTION_MIN_WIDTH}>Description</TableHead>
                <TableHead className="w-14 min-w-14">Variants</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {CODEX_TILE_KINDS.map((kind) => (
                <CodexTileRow key={kind} kind={kind} />
              ))}
            </TableBody>
          </Table>
        </Card>
      </InfotipProvider>
    </section>
  );
}
